import { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import UsbOutlinedIcon from "@mui/icons-material/UsbOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { usePanelTokens } from "../../../utils/panelTokens";
import { parseApj, parseBin, flash, readBoardInfo } from "../../../lib/px4Bootloader";
import { listFirmwareBuilds, downloadFirmwareBuild } from "./api/firmware";

const SUPPORTED = typeof navigator !== "undefined" && "serial" in navigator;
const BAUD_RATES = [115200, 230400, 460800, 921600];

/** Bordered panel with the title sitting on the border — native fieldset/legend. */
const Section = ({ title, children, t, sx }) => (
  <Box
    component="fieldset"
    sx={{
      border: `1px solid ${t.border}`,
      borderRadius: 2,
      background: t.surface,
      px: 2.5,
      pb: 2.5,
      pt: 1,
      mb: 2.5,
      minWidth: 0,
      ...sx,
    }}
  >
    <Box
      component="legend"
      sx={{ px: 1, fontSize: 14, fontWeight: 600, color: t.textStrong }}
    >
      {title}
    </Box>
    {children}
  </Box>
);

const Field = ({ label, value, t }) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
    <Typography sx={{ width: 190, textAlign: "right", fontWeight: 600, fontSize: 14, color: t.text }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, color: value ? t.text : t.textFaint }}>
      {value || "—"}
    </Typography>
  </Box>
);

const FirmwareFlasher = () => {
  const t = usePanelTokens();
  const fileRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [fw, setFw] = useState(null);
  const [board, setBoard] = useState(null);
  const [baudRate, setBaudRate] = useState(115200);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [log, setLog] = useState([]);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [builds, setBuilds] = useState(null);
  const [loadingBuilds, setLoadingBuilds] = useState(false);

  const say = (line) => setLog((l) => [...l, line]);
  const reset = () => {
    setError(null);
    setDone(false);
    setLog([]);
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reset();
    try {
      const parsed = file.name.endsWith(".apj")
        ? await parseApj(await file.text())
        : parseBin(await file.arrayBuffer());
      setFw({ ...parsed, name: file.name });
    } catch (err) {
      setFw(null);
      setError(err.message);
    }
  };

  // Narrowed to the detected board when we have one — a list of builds that
  // physically cannot flash is worse than no list.
  const loadBuilds = async () => {
    reset();
    setLoadingBuilds(true);
    try {
      setBuilds(await listFirmwareBuilds({ boardId: board?.boardId }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingBuilds(false);
    }
  };

  const chooseBuild = async (build) => {
    reset();
    setLoadingBuilds(true);
    try {
      const buffer = await downloadFirmwareBuild(build);
      const parsed = await parseApj(new TextDecoder().decode(buffer));
      setFw({ ...parsed, name: `${build.vehicle} ${build.version} · ${build.board}` });
    } catch (err) {
      setFw(null);
      setError(err.message);
    } finally {
      setLoadingBuilds(false);
    }
  };

  const detect = async () => {
    reset();
    let port;
    try {
      port = await navigator.serial.requestPort();
    } catch {
      return; // picker dismissed
    }
    setBusy(true);
    try {
      const info = await readBoardInfo(port, { baudRate });
      setBoard(info);
      say(`Detected board ID ${info.boardId} rev ${info.boardRev}, bootloader rev ${info.blRev}.`);
    } catch (err) {
      setBoard(null);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    reset();
    let port;
    try {
      port = await navigator.serial.requestPort();
    } catch {
      return;
    }
    setTab(1);
    setBusy(true);
    try {
      await flash(port, fw, { onLog: say, onProgress: setProgress, baudRate });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const mismatch = fw && board && fw.boardId != null && fw.boardId !== board.boardId;

  return (
    <Box sx={{ color: t.text, pb: 12 }}>
      <Box sx={{ px: 3, pt: 3, maxWidth: 1000, mx: "auto" }}>
        <Typography variant="h5" sx={{ color: t.textStrong, fontWeight: 600 }}>
          Firmware Flasher
        </Typography>
        <Box sx={{ borderBottom: `1px solid ${t.border}`, mt: 2, mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Board & Firmware" />
            <Tab label="Flash" />
          </Tabs>
        </Box>

        {!SUPPORTED && (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            This browser has no Web Serial support. Use Chrome or Edge on desktop, or take the
            firmware from Downloads and flash it with Mission Planner.
          </Alert>
        )}

        {tab === 0 && (
          <>
            <Section title="Board Selection" t={t}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={<TravelExploreOutlinedIcon />}
                  onClick={detect}
                  disabled={busy || !SUPPORTED}
                >
                  Detect
                </Button>
                <Typography sx={{ fontSize: 14, color: t.textMuted }}>
                  Reads the board ID over USB without writing anything.
                </Typography>
              </Box>
              {board && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${t.borderSoft}` }}>
                  <Field label="Board ID:" value={`${board.boardId} (rev ${board.boardRev})`} t={t} />
                  <Field label="Bootloader:" value={`rev ${board.blRev}`} t={t} />
                  <Field label="Flash:" value={`${(board.flashSize / 1024).toFixed(0)} KB`} t={t} />
                </Box>
              )}
            </Section>

            <Section title="Firmware" t={t}>
              <input
                ref={fileRef}
                type="file"
                accept=".apj,.bin"
                onChange={pickFile}
                style={{ display: "none" }}
              />
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" onClick={() => fileRef.current?.click()} disabled={busy}>
                  Load Firmware [Local]
                </Button>
                <Tooltip title="Vecros-hosted ArduPilot builds">
                  <span>
                    <Button
                      variant="outlined"
                      onClick={loadBuilds}
                      disabled={busy || loadingBuilds}
                    >
                      {loadingBuilds ? "Loading…" : "Load Firmware [Online]"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Typography sx={{ color: t.textFaint, fontSize: 13, mt: 1.5 }}>
                {board
                  ? `Online builds are filtered to board ID ${board.boardId}.`
                  : "Run Detect first and the online list narrows to builds that fit your board."}
              </Typography>

              {builds?.length === 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  No hosted builds{board ? ` for board ID ${board.boardId}` : ""} yet. Load a
                  local .apj instead.
                </Alert>
              )}
              {builds?.length > 0 && (
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  defaultValue=""
                  sx={{ mt: 1.5 }}
                  disabled={loadingBuilds || busy}
                  onChange={(e) => chooseBuild(builds.find((b) => b.id === e.target.value))}
                >
                  <MenuItem value="" disabled>
                    Choose a firmware version…
                  </MenuItem>
                  {builds.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.vehicle} {b.version} · {b.board}
                      {b.release_type && b.release_type !== "stable" ? ` (${b.release_type})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              )}

              {fw && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${t.borderSoft}` }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {fw.summary && <Chip size="small" label={fw.summary} />}
                    {fw.version && <Chip size="small" label={fw.version} />}
                    <Chip
                      size="small"
                      color={fw.boardId != null ? "default" : "warning"}
                      label={fw.boardId != null ? `Board ID ${fw.boardId}` : "No board ID"}
                    />
                    <Chip size="small" label={`${(fw.image.length / 1024).toFixed(0)} KB`} />
                  </Box>
                  {fw.boardId == null && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      A raw .bin carries no board ID, so nothing can check it fits this board.
                      Prefer the .apj build.
                    </Alert>
                  )}
                </Box>
              )}
            </Section>
          </>
        )}

        {tab === 1 && (
          <>
            <Section title="Target" t={t}>
              <Field label="Board:" value={board ? `ID ${board.boardId} rev ${board.boardRev}` : ""} t={t} />
              <Field label="Firmware:" value={fw?.summary || fw?.name} t={t} />
              <Field label="Version:" value={fw?.version} t={t} />
              <Field label="Size:" value={fw ? `${(fw.image.length / 1024).toFixed(0)} KB` : ""} t={t} />
              <Field label="Git hash:" value={fw?.gitIdentity} t={t} />
            </Section>

            <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr 1fr" }, gap: 2.5, mb: 2.5 }}>
              <Alert severity="error" sx={{ m: 0 }}>
                <strong>Warning</strong>
                <br />
                Do not disconnect the board during flashing. Firmware is matched against the
                board ID and verified by CRC before reboot, but a flash interrupted midway
                leaves a partial image that must be reflashed before flying.
              </Alert>
              <Alert severity="warning" sx={{ m: 0 }}>
                <strong>Recovery / lost communication</strong>
                <br />
                The bootloader listens for a few seconds after power-up. If Detect finds nothing:
                unplug the board, press Flash, pick the port, then plug it back in. A board with
                no bootloader at all needs DFU over BOOT0 — not supported here yet.
              </Alert>
            </Box>

            <Section title="Advanced" t={t}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Select
                  size="small"
                  value={baudRate}
                  onChange={(e) => setBaudRate(e.target.value)}
                  disabled={busy}
                >
                  {BAUD_RATES.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
                <Typography sx={{ fontSize: 14, color: t.textMuted }}>
                  Baud rate — ignored over native USB, matters on a UART adapter.
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: t.textFaint, mt: 1.5 }}>
                Full chip erase and CRC verification always run; neither is optional.
              </Typography>
            </Section>

            {progress && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: t.textMuted, mb: 0.5 }}>
                  {progress.phase} · {progress.percent.toFixed(0)}%
                </Typography>
                <LinearProgress
                  variant={progress.phase === "erase" ? "indeterminate" : "determinate"}
                  value={progress.percent}
                />
              </Box>
            )}
            {busy && (
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Do not unplug the board or close this tab until it finishes.
              </Alert>
            )}
            {done && (
              <Alert severity="success" sx={{ mb: 2.5 }}>
                Flashed and verified. The board has rebooted into the new firmware.
              </Alert>
            )}
            {log.length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  background: t.surfaceAlt,
                  border: `1px solid ${t.borderSoft}`,
                  fontFamily: "monospace",
                  fontSize: 12,
                  color: t.textMuted,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {log.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </Box>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        )}
        {mismatch && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            This firmware is built for board ID {fw.boardId}, but the connected board is ID{" "}
            {board.boardId}. Flashing is blocked.
          </Alert>
        )}
      </Box>

      {/* Sticky action bar, Betaflight-style — the flash button is always reachable. */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          mt: 2,
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          alignItems: "center",
          background: t.surface,
          borderTop: `1px solid ${t.border}`,
        }}
      >
        {!fw && (
          <Typography sx={{ fontSize: 13, color: t.textFaint }}>
            Load a firmware file to enable flashing.
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<UsbOutlinedIcon />}
          onClick={run}
          disabled={!fw || busy || mismatch || !SUPPORTED}
        >
          {busy ? "Flashing…" : "Flash Firmware"}
        </Button>
      </Box>
    </Box>
  );
};

export default FirmwareFlasher;
